import { lazy, Suspense, useState, useEffect } from "react"
import PropTypes from "prop-types"
import Mustache from "mustache"

import { buildFilterContextDataForDIY } from "shared/components/filter_code/build_filter_context_data"
import ErrorMessageForField from "./error_message"
import { loadFilterCodeGenerators, validateFilterCode } from "./api"
import { trackStateChangeFilterCodeCreated } from "./utils/telemetry"

import generateApiList from "./filter_code/api_list.tsx"
import FilterCodeIngredientListbox from "./filter_code/ingredient_listbox.tsx"
import GeneratorModal from "./filter_code/generator_modal.tsx"

import "./filter_code.scss"

const MonacoEditor = lazy(() => import("shared/components/monaco_editor"))

export default function FilterCode({
  contextTemplate,
  selectedTrigger,
  selectedQueries,
  selectedActions,
  filterCode,
  updateFilterCode,
}) {
  const contextData = buildFilterContextDataForDIY({
    selectedTrigger,
    selectedQueries,
    selectedActions,
  })
  const apiList = generateApiList(contextData)

  const filterCodePlaceholder = `// Add your code here. All actions will run unless you explicitly skip them.
// Quick tips!
// Auto-complete is on. Start typing to see ingredient options.
// Hover over any ingredient to see the variable type and an example.
// TypeScript v2.92
`
  const [code, updateCode] = useState(filterCode || filterCodePlaceholder)
  const [generators, updateGenerators] = useState([])
  const [selectedGenerator, updateSelectedGenerator] = useState(null)
  const [errors, updateErrors] = useState([])
  const [submitDisabled, updateSubmitDisabled] = useState(false)

  useEffect(() => {
    loadFilterCodeGenerators(
      [selectedTrigger.id],
      selectedQueries.map(q => q["id"]),
      selectedActions.map(a => a["id"])
    ).then(result => {
      updateGenerators(result)
    })
  }, [])

  useEffect(() => {
    if (errors.length) {
      window.scrollTo({ top: 0, behavior: "smooth" })
    }
  }, [errors])

  const buttonText = () => {
    let text = "Add filter"

    if (filterCode === "") {
      text = submitDisabled ? "Adding filter..." : "Add filter"
    } else {
      text = submitDisabled ? "Updating filter..." : "Update filter"
    }

    return text
  }

  const onClick = () => {
    updateSubmitDisabled(true)
    trackStateChangeFilterCodeCreated()

    validateFilterCode({
      filter_code: code,
      trigger: selectedTrigger,
      queries: selectedQueries,
      actions: selectedActions,
    })
      .then(({ errors }) => {
        if (errors.length) {
          updateErrors(errors)
          updateSubmitDisabled(false)
        } else {
          updateFilterCode(code)
        }
      })
      .catch(() => {
        updateErrors([{ message: "Unable to validate filter code" }])
        updateSubmitDisabled(false)
      })
  }

  return (
    <div styleName="filter-code">
      <section styleName="left-panel">
        {errors.map(e => (
          <ErrorMessageForField key={e.message} errors={e} field="message" />
        ))}
        <Suspense fallback="Loading...">
          <MonacoEditor
            positionAbsolute={false}
            // MonacoEditor on filter code uses all available space in their zone. It's no possible to use height 100% because it's expected a blocked height for
            // component to calculate the JS height. 182px is the size from top (header + extra space) to the component.
            height="calc(100vh - 182px)"
            defaultValue={code}
            onChange={updateCode}
            typescriptContext={Mustache.render(contextTemplate, contextData)}
            location_type="diy_composer"
          />
        </Suspense>
      </section>

      <section styleName="right-panel">
        <div styleName="cta">
          <button
            className="button-tertiary"
            disabled={submitDisabled}
            onClick={onClick}
          >
            {buttonText()}
          </button>
        </div>

        <FilterCodeIngredientListbox
          apiList={apiList["Triggers"]}
          name="Triggers"
          serviceList={[selectedTrigger.service]}
        />

        <FilterCodeIngredientListbox
          apiList={apiList["Queries"]}
          name="Queries"
          serviceList={selectedQueries.map(q => q.service)}
        />

        <FilterCodeIngredientListbox
          apiList={apiList["Actions"]}
          name="Actions"
          serviceList={selectedActions.map(q => q.service)}
        />

        <FilterCodeIngredientListbox apiList={apiList["More"]} name="More" />

        {generators && (
          <section styleName="generators">
            <h4 className="mt4 mb2">Code generators</h4>
            <ul>
              {generators.map(generator => (
                <li key={generator.id} className="mb2">
                  <a
                    className="link-standalone"
                    onClick={() => updateSelectedGenerator(generator.id)}
                    data-track-ifttt-next-event="click"
                    data-track-data={JSON.stringify({
                      object_type: `generator`,
                      object_id: generator.id,
                      location_type: "diy_filter_code",
                    })}
                  >
                    {generator.title}
                  </a>
                </li>
              ))}
            </ul>
            {selectedGenerator && (
              <GeneratorModal
                generator={generators.find(g => g.id === selectedGenerator)}
                onClose={() => updateSelectedGenerator(null)}
              />
            )}
          </section>
        )}
      </section>
    </div>
  )
}

FilterCode.propTypes = {
  contextTemplate: PropTypes.string.isRequired,
  selectedTrigger: PropTypes.object,
  selectedQueries: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedActions: PropTypes.arrayOf(PropTypes.object).isRequired,
  filterCode: PropTypes.string,
  updateFilterCode: PropTypes.func.isRequired,
}

FilterCode.defaultProps = {
  filterCode: "",
}
